import { Injectable, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatSessionService } from '../pages/ai-chat/services/chat-session.service';
import { UiLayoutService } from '../ui-layout/services/ui-layout.service';
import { AlertService } from '../shared/alert/alert.service';
import { ApiService } from './api.service';
import { AlertEnum } from '../shared/alert/alert.enum';
import { ISessionState } from '../interfaces/i-session-state';
import { SessionStatus } from '../enums/session-status.enum';
import { environment } from '../../environments/environment';
import { ExecutionStatusEnum, InstructionType } from '../enums/automation.enum';
import { IInstructions } from '../pages/automation/interfaces/i-instructions';

@Injectable({
  providedIn: 'root'
})
export class MultiSessionService {
  // Made a var for max concurrent sessions to make adjusting this value easier, for the future and beyond!
  // This value is pulled from the environment config, so it can be adjusted without code changes.
  // Also added the "+" to convert the string value from environment to a number.
  readonly MAX_CONCURRENT_SESSIONS = +environment.maxConcurrentSessions;
  
  // Track session states for all sessions
  #sessionStates = signal<Map<string | number, ISessionState>>(new Map());
  
  // Track up to the MAX_CONCURRENT_SESSIONS concurrently submitting session IDs
  #activeSubmittingSessions = signal<Set<string | number>>(new Set());

  #chatSessionService = inject(ChatSessionService);
  #uiLayoutService = inject(UiLayoutService);
  #alertService = inject(AlertService);
  #apiService = inject(ApiService);

  /**
   * Gets the current active submitting sessions
   */
  get activeSubmittingSessions(): Set<string | number> {
    return this.#activeSubmittingSessions();
  }

  /**
   * Gets the session state for a specific session
   */
  getSessionState(sessionId: string | number): ISessionState | undefined {
    return this.#sessionStates().get(sessionId);
  }

  /**
   * Checks if we can start a new session (haven't reached max concurrent sessions)
   */
  canStartNewSession(sessionId: string | number): boolean {
    const activeSubmitting = this.#activeSubmittingSessions();
    return !activeSubmitting.has(sessionId) && activeSubmitting.size < this.MAX_CONCURRENT_SESSIONS;
  }

  /**
   * Checks if max concurrent sessions has been reached
   */
  hasReachedMaxConcurrency(): boolean {
    return this.#activeSubmittingSessions().size >= this.MAX_CONCURRENT_SESSIONS;
  }

  /**
   * Shows an alert when max concurrent sessions is reached
   */
  showMaxConcurrencyAlert(): void {
    this.#alertService.showAlert({
      message: `You can only run up to ${this.MAX_CONCURRENT_SESSIONS} sessions concurrently. Please wait for one to finish processing and try again later.`,
      type: AlertEnum.error,
    });
  }

  /**
   * Starts tracking a session as actively submitting
   */
  startSession(sessionId: string | number, subscription?: Subscription, abortController?: AbortController, messageId?: string, meta?: any): void {
    // Cancel any existing stream for this session first
    this.cancelSession(sessionId);
    
    // Add to active submitting sessions
    this.#activeSubmittingSessions.update(sessions => {
      const newSessions = new Set(sessions);
      newSessions.add(sessionId);
      return newSessions;
    });

    // Update session state - preserve existing state to avoid clearing the pause flags
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      const currentState = newStates.get(sessionId);
      
      newStates.set(sessionId, {
        ...currentState, // Preserve existing state (pause flags, etc.)
        isSubmitting: true,
        streamSubscription: subscription,
        abortController,
        messageId,
        meta: {
          ...currentState?.meta, // Preserve existing meta
          ...meta // Merge with new meta
        }
      });
      return newStates;
    });

    // Update UI status
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.PROCESSING);
    
    // Update session object with lastStatus
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s => 
        s.id === sessionId 
          ? { ...s, lastStatus: SessionStatus.PROCESSING }
          : s
      );
    });
  }

  /**
   * Completes a session and cleans up its state
   */
  completeSession(sessionId: string | number, status?: ExecutionStatusEnum): void {
    // Map ExecutionStatusEnum to SessionStatus
    let finalStatus: SessionStatus;
    if (!status || status === ExecutionStatusEnum.Cancelled) {
      // User-initiated cancellation or no status provided - use default icon
      finalStatus = SessionStatus.DEFAULT;
    } else if (status === ExecutionStatusEnum.Success) {
      finalStatus = SessionStatus.SUCCESS;
    } else if (status === ExecutionStatusEnum.Failed) {
      finalStatus = SessionStatus.ERROR;
    } else if (status === ExecutionStatusEnum.InProgress || status === ExecutionStatusEnum.Waiting) {
      finalStatus = SessionStatus.PROCESSING;
    } else if (status === ExecutionStatusEnum.UserInput) {
      finalStatus = SessionStatus.PAUSED;
    } else {
      // Fallback to default for any other status
      finalStatus = SessionStatus.DEFAULT;
    }
    
    // Update UI with final status
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, finalStatus);
    
    // Update session object with lastStatus
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s => 
        s.id === sessionId 
          ? { ...s, lastStatus: finalStatus }
          : s
      );
    });
    
    // Remove from active submitting sessions
    this.#activeSubmittingSessions.update(sessions => {
      const newSessions = new Set(sessions);
      newSessions.delete(sessionId);
      return newSessions;
    });

    // Clean up session state
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      newStates.delete(sessionId);
      return newStates;
    });
  }

  /**
   * Handles session error and cleans up state
   */
  errorSession(sessionId: string | number): void {
    // Update UI to error state
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.ERROR);
    
    // Update session object with lastStatus
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s => 
        s.id === sessionId 
          ? { ...s, lastStatus: SessionStatus.ERROR }
          : s
      );
    });
    
    // Remove from active submitting sessions
    this.#activeSubmittingSessions.update(sessions => {
      const newSessions = new Set(sessions);
      newSessions.delete(sessionId);
      return newSessions;
    });

    // Clean up session state
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      newStates.delete(sessionId);
      return newStates;
    });
  }

  /**
   * Pauses a session and updates its state
   */
  pauseSession(sessionId: string | number, workflowId: string): void {
    // Update UI to pause state
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.PAUSED);
    
    // Update session object with lastStatus
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s => 
        s.id === sessionId 
          ? { ...s, lastStatus: SessionStatus.PAUSED }
          : s
      );
    });
    
    // Update session state to include pause information
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      const currentState = newStates.get(sessionId);
      
      if (currentState) {
        newStates.set(sessionId, {
          ...currentState,
          isSubmitting: false,
          meta: {
            ...currentState.meta,
            workflowId,
            isPaused: true
          }
        });
      }
      
      return newStates;
    });
  }

  /**
   * Pauses a session and marks it as waiting for user input
   */
  pauseSessionForUserInput(
    sessionId: string | number, 
    inputType: InstructionType.CollectInformation | InstructionType.FileUpload,
    workflowId?: string,
    instructionId?: string,
    instruction?: IInstructions
  ): void {
    // Update UI to pause state
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.PAUSED);
    
    // Update session object with lastStatus
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s => 
        s.id === sessionId 
          ? { ...s, lastStatus: SessionStatus.PAUSED }
          : s
      );
    });
    
    // Update session state to include pause and user input information
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      const currentState = newStates.get(sessionId);
      
      if (currentState) {
        newStates.set(sessionId, {
          ...currentState,
          isSubmitting: false,
          isWaitingForUserInput: inputType === InstructionType.CollectInformation,
          isWaitingForFileUpload: inputType === InstructionType.FileUpload,
          userInputType: inputType,
          workflowId,
          instructionId,
          instructionInProgress: instruction,
          meta: {
            ...currentState.meta,
            workflowId,
            isPaused: true
          }
        });
      } else {
        // Create new state if it doesn't exist
        newStates.set(sessionId, {
          isSubmitting: false,
          isWaitingForUserInput: inputType === InstructionType.CollectInformation,
          isWaitingForFileUpload: inputType === InstructionType.FileUpload,
          userInputType: inputType,
          workflowId,
          instructionId,
          instructionInProgress: instruction,
          meta: {
            workflowId,
            isPaused: true
          }
        });
      }
      
      return newStates;
    });

    // TODO: Leaving this code here in case, down the line, we consider a "paused" session to not be an actively submitting session.
    // Remove from active submitting sessions
    // this.#activeSubmittingSessions.update(sessions => {
    //   const newSessions = new Set(sessions);
    //   newSessions.delete(sessionId);
    //   return newSessions;
    // });
  }

  /**
   * Checks if a session is waiting for user input (text or file)
   */
  isSessionWaitingForInput(sessionId: string | number): boolean {
    const state = this.getSessionState(sessionId);
    return !!(state?.isWaitingForUserInput || state?.isWaitingForFileUpload);
  }

  /**
   * Gets the type of user input a session is waiting for
   */
  getSessionInputType(sessionId: string | number): InstructionType.CollectInformation | InstructionType.FileUpload | null {
    return this.getSessionState(sessionId)?.userInputType ?? null;
  }

  /**
   * Clears user input waiting state for a session
   */
  clearUserInputState(sessionId: string | number): void {
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      const currentState = newStates.get(sessionId);
      
      if (currentState) {
        newStates.set(sessionId, {
          ...currentState,
          isWaitingForUserInput: false,
          isWaitingForFileUpload: false,
          userInputType: null
        });
      }
      
      return newStates;
    });
  }

  /**
   * Resumes a session that was paused for user input
   * Returns true if the session was resumed, false if it wasn't waiting for input
   */
  resumeSessionFromUserInput(sessionId: string | number): boolean {
    const state = this.getSessionState(sessionId);
    
    if (!state || !this.isSessionWaitingForInput(sessionId)) {
      return false;
    }

    // Clear user input state
    this.clearUserInputState(sessionId);
    
    // Update UI to processing state
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.PROCESSING);
    
    // Update session object with lastStatus
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s => 
        s.id === sessionId 
          ? { ...s, lastStatus: SessionStatus.PROCESSING }
          : s
      );
    });
    
    return true;
  }

  /**
   * Cancels a specific session stream and cleans up
   */
  cancelSession(sessionId: string | number): void {
    const sessionState = this.getSessionState(sessionId);
    
    if (sessionState?.streamSubscription) {
      sessionState.streamSubscription.unsubscribe();
    }
    
    if (sessionState?.abortController) {
      sessionState.abortController.abort();
    }

    // Remove from active submitting sessions
    this.#activeSubmittingSessions.update(sessions => {
      const newSessions = new Set(sessions);
      newSessions.delete(sessionId);
      return newSessions;
    });

    // Clean up streaming state but preserve pause-related state
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      const currentState = newStates.get(sessionId);
      
      // If session has pause state, preserve it (don't fully delete)
      if (currentState?.isWaitingForUserInput || currentState?.isWaitingForFileUpload) {
        newStates.set(sessionId, {
          isSubmitting: false,
          isWaitingForUserInput: currentState.isWaitingForUserInput,
          isWaitingForFileUpload: currentState.isWaitingForFileUpload,
          userInputType: currentState.userInputType,
          workflowId: currentState.workflowId,
          instructionId: currentState.instructionId,
          meta: currentState.meta
        });
      } else {
        // No pause state, fully delete
        newStates.delete(sessionId);
      }
      
      return newStates;
    });

    // Set icon to DEFAULT when user manually cancels
    // Note: When called from startSession(), this will be immediately overwritten by the PROCESSING state
    // When called from manual cancel (cancelStreamingMessage), this sets the correct DEFAULT state
    if (!sessionState?.isWaitingForUserInput && !sessionState?.isWaitingForFileUpload) {
      this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.DEFAULT);
    }
  }

  /**
   * Cancels all active sessions
   */
  cancelAllSessions(): void {
    const sessionStates = this.#sessionStates();
    
    // Cancel all active sessions
    sessionStates.forEach((state, sessionId) => {
      if (state.streamSubscription) {
        state.streamSubscription.unsubscribe();
      }
      
      if (state.abortController) {
        state.abortController.abort();
      }

      // Update UI to default state
      this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.DEFAULT);
    });

    // Clear all session tracking
    this.#activeSubmittingSessions.set(new Set());
    this.#sessionStates.set(new Map());
  }

  /**
   * Updates session state with new subscription/abort controller
   */
  updateSessionState(sessionId: string | number, updates: Partial<ISessionState>): void {
    this.#sessionStates.update(states => {
      const newStates = new Map(states);
      const currentState = newStates.get(sessionId);
      
      if (currentState) {
        newStates.set(sessionId, {
          ...currentState,
          ...updates
        });
      }
      
      return newStates;
    });
  }

  /**
   * Gets the message ID for a specific session
   */
  getSessionMessageId(sessionId: string | number): string | undefined {
    return this.getSessionState(sessionId)?.messageId;
  }

  /**
   * Gets the meta data for a specific session
   */
  getSessionMeta(sessionId: string | number): any {
    return this.getSessionState(sessionId)?.meta;
  }

  /**
   * Checks if any session is currently streaming
   */
  isAnySessionStreaming(): boolean {
    return this.#activeSubmittingSessions().size > 0;
  }

  /**
   * Checks if a specific session is currently streaming
   */
  isSessionStreaming(sessionId: string | number): boolean {
    return this.#activeSubmittingSessions().has(sessionId);
  }

  /**
   * Gets the workflow ID for a session that is paused for user input
   */
  getSessionWorkflowId(sessionId: string | number): string | undefined {
    return this.getSessionState(sessionId)?.workflowId;
  }

  /**
   * Gets the instruction ID for a session that is paused for user input
   */
  getSessionInstructionId(sessionId: string | number): string | undefined {
    return this.getSessionState(sessionId)?.instructionId;
  }

  /**
   * Gets the instruction in progress for a paused session
   */
  getSessionInstructionInProgress(sessionId: string | number): IInstructions | undefined {
    return this.getSessionState(sessionId)?.instructionInProgress;
  }

  /**
   * Marks a session as "read" by resetting Success/Error status to Default
   * Similar to marking an unread message as read when the user navigates to it
   */
  markSessionAsRead(sessionId: string | number): void {
    const sessions = this.#chatSessionService.chatSessions();
    const session = sessions?.find(s => s.id === sessionId);
    if (!session) return;
    
    // Only reset status if it's Success or Error
    if (!session || ![SessionStatus.SUCCESS, SessionStatus.ERROR].includes(session.lastStatus!)) {
      return;
    }
    
    const newStatus = SessionStatus.DEFAULT;
    
    // Update UI icon
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, newStatus);
    
    // Update in-memory session
    session.lastStatus = newStatus;
    
    // Update sessions signal
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s => 
        s.id === sessionId 
          ? { ...s, lastStatus: newStatus }
          : s
      );
    });
    
    // Persist to backend
    this.#apiService.persistSession(session).subscribe();
  }
}
